import {
  useESSnackbar,
  ESButton,
  ESDialogActions,
  ESDialogContent,
  ESDialogTitle,
  ESSelect,
} from '@energy-stacks/core/ui';
import { Dialog, Stack, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import {
  ChargingStationDetailsModel,
  changeAvailability21RequestOptions,
  ChangeAvailability21RequestOption,
  useChangeChargingStationAvailability21Mutation,
} from '@energy-stacks/broker/feature-charging-stations-data';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface ChangeChargingStationAvailabilityStatusDialogV21Props {
  isOpen: boolean;
  onClose: () => void;
  identityKey: ChargingStationDetailsModel['identityKey'];
  currentStatus: ChargingStationDetailsModel['csStatus'];
}

export const ChangeChargingStationAvailabilityStatusDialogV21: React.FC<
  ChangeChargingStationAvailabilityStatusDialogV21Props
> = ({ identityKey, currentStatus, isOpen, onClose }) => {
  const { t } = useTranslation('chargingStations');
  const { showSnackbar } = useESSnackbar();
  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
    getValues,
  } = useForm<{ availability: ChangeAvailability21RequestOption }>({
    resolver: yupResolver(
      yup.object().shape({
        availability: yup
          .string()
          .required()
          .oneOf([...changeAvailability21RequestOptions]),
      })
    ),
    mode: 'onChange',
    defaultValues: {
      availability: undefined,
    },
  });
  const [triggerChangeChargingStationAvailability, { isLoading }] =
    useChangeChargingStationAvailability21Mutation();
  const requestRef =
    useRef<ReturnType<typeof triggerChangeChargingStationAvailability>>();

  const onSubmit = useCallback(() => {
    const changeAvailability = triggerChangeChargingStationAvailability({
      identityKey: identityKey,
      operationalStatus: getValues().availability,
    });

    requestRef.current = changeAvailability;

    changeAvailability
      .unwrap()
      .then((result) => {
        if (result.status === 'Rejected') {
          showSnackbar(
            'error',
            t('changeChargingStationStatusRejectedMessage')
          );
          return;
        }

        if (result.status === 'Accepted') {
          showSnackbar(
            'success',
            t('changeChargingStationStatusSuccessMessage')
          );
        }

        if (result.status === 'Scheduled') {
          showSnackbar(
            'info',
            t('changeChargingStationStatusScheduledMessage')
          );
        }

        onClose();
      })
      .catch((error) => {
        if (error.data?.errorCode === 'RPC_TIMEOUT') {
          showSnackbar('error', t('rpcTimeoutError'));
          return;
        }
        if (error.data?.errorCode === 'RPC_ERROR') {
          showSnackbar('error', t('rpcError'));
          return;
        }

        if (error.name === 'AbortError') {
          return;
        }

        showSnackbar('error');
      });
  }, [
    identityKey,
    triggerChangeChargingStationAvailability,
    getValues,
    onClose,
    t,
    showSnackbar,
  ]);

  const handleCancel = useCallback(() => {
    requestRef.current?.abort();
    reset();
    onClose();
  }, [onClose, reset]);

  return (
    <Dialog open={isOpen} onClose={handleCancel} fullWidth maxWidth="sm">
      <ESDialogTitle data-testid="changeStatusTitle">
        {t('changeStatus')}
      </ESDialogTitle>

      <ESDialogContent>
        <Stack direction="column" data-testid="changeStatusContent">
          <Typography variant="caption" color="text.secondary">
            {t('currentStatus')}
          </Typography>
          <Typography>
            {currentStatus
              ? t(currentStatus, { context: 'status' })
              : t('unknown')}
          </Typography>
        </Stack>

        <Controller
          control={control}
          name="availability"
          render={({ field: { value, onChange, onBlur } }) => (
            <ESSelect
              testId="stationAvailability"
              label={t('newStatus')}
              options={changeAvailability21RequestOptions.map((option) => ({
                value: option,
                label: t(
                  `changeAvailabilityRequestStatusOption.${
                    option === 'Operative' ? 'Available' : 'Unavailable'
                  }`
                ),
              }))}
              onChange={onChange}
              onBlur={onBlur}
              value={value || ''}
              required
              disabled={isLoading}
              sx={{ mt: 8 }}
            />
          )}
        />
      </ESDialogContent>

      <ESDialogActions>
        <ESButton
          data-testid="changeStatusCancelButton"
          variant="text"
          color="error"
          onClick={handleCancel}
          disabled={isLoading}
        >
          {t('cancel')}
        </ESButton>

        <ESButton
          data-testid="changeStatusChangeButton"
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          disabled={!isValid || isLoading}
          loading={isLoading}
        >
          {t('change')}
        </ESButton>
      </ESDialogActions>
    </Dialog>
  );
};
